import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useResolveAsset } from '../context';
import Lightbox from './Lightbox';

export type FigureAlign = 'left' | 'right' | 'center' | 'full';

type FigureProps = {
  src: string;
  alt?: string;
  caption?: React.ReactNode;
  /** left/right float (text wraps), centered, or full-bleed. Default: center. */
  align?: FigureAlign;
  /** Override the default per-align width (e.g. "30%", 480). */
  width?: number | string;
};

// styled() over sx: this is structural and reused for every image in every
// post. Floats collapse to full width on xs so mobile never wraps text
// around a tiny image. $-prefixed props are transient (not forwarded to DOM).
const Frame = styled('figure', {
  shouldForwardProp: (prop) => prop !== '$align' && prop !== '$width',
})<{ $align: FigureAlign; $width?: number | string }>(({ theme, $align, $width }) => ({
  margin: theme.spacing(3, 0),
  '& img': {
    display: 'block',
    width: '100%',
    height: 'auto',
    borderRadius: theme.shape.borderRadius,
    cursor: 'zoom-in',
  },
  ...($align === 'center' && {
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: $width ?? 760,
  }),
  ...($align === 'full' && { width: $width ?? '100%' }),
  ...(($align === 'left' || $align === 'right') && {
    [theme.breakpoints.up('sm')]: {
      width: $width ?? '42%',
      float: $align,
      marginTop: theme.spacing(1),
      [$align === 'left' ? 'marginRight' : 'marginLeft']: theme.spacing(3),
    },
  }),
}));

// `as typeof Typography` keeps the OverridableComponent typing so
// `component="figcaption"` stays type-checked (per docs/styling/mui-styling.md).
const Caption = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
  textAlign: 'center',
  fontStyle: 'italic',
})) as typeof Typography;

/**
 * Positional, captioned image for MDX blogs. `src` is author-relative
 * (`images/foo.jpg`) and resolved against the blog's CDN prefix. Clicking the
 * image opens it in the shared centered lightbox.
 */
const Figure: React.FC<FigureProps> = ({ src, alt = '', caption, align = 'center', width }) => {
  const resolveAsset = useResolveAsset();
  const [open, setOpen] = React.useState<number | null>(null);
  return (
    <Frame $align={align} $width={width}>
      <img src={resolveAsset(src)} alt={alt} loading="lazy" onClick={() => setOpen(0)} />
      {caption != null && (
        <Caption variant="caption" component="figcaption">
          {caption}
        </Caption>
      )}
      <Lightbox
        items={[{ src, alt, caption: typeof caption === 'string' ? caption : undefined }]}
        index={open}
        onIndex={setOpen}
      />
    </Frame>
  );
};

export default Figure;
